(() => {
  const products = new Map();
  class CCProduct {
    constructor(root) {
      this.root = root;
      this.controller = new AbortController();
      this.request = null;
      this.data = JSON.parse(root.querySelector('[data-cc-product-data]').textContent);
      this.form = root.querySelector('[data-cc-product-form]');
      this.quantity = root.querySelector('[data-cc-quantity]');
      this.idInput = root.querySelector('[data-cc-variant-id]');
      this.sticky = root.querySelector('[data-product-sticky]');
      this.primary = root.querySelector('[data-product-primary-atc]');
      this.error = root.querySelector('[data-cc-product-error]');
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style: 'currency', currency: root.dataset.currency || 'USD'});
      this.idInput.disabled = false;
      const options = {signal: this.controller.signal};
      root.addEventListener('change', event => this.change(event), options);
      root.addEventListener('click', event => this.click(event), options);
      root.addEventListener('keydown', event => this.keydown(event), options);
      root.addEventListener('close', () => this.syncDialogLock(), {...options, capture: true});
      window.addEventListener('resize', () => this.updateSticky(), options);
      window.addEventListener('scroll', () => this.scheduleSticky(), {...options, passive: true});
      this.updateVariant(this.data, false);
      this.setStickyObserver();
      this.loadRecommendations();
    }
    format(cents) { return this.money.format(Number(cents || 0) / 100); }
    change(event) {
      const select = event.target.closest('[data-cc-option]');
      if (select) {
        for (const peer of this.root.querySelectorAll('[data-cc-option]')) {
          if (peer.dataset.optionIndex === select.dataset.optionIndex) peer.value = select.value;
        }
        this.loadVariant();
      }
      if (event.target === this.quantity) this.updateQuantity();
    }
    selectedOptionValues() {
      return [...this.form.querySelectorAll('[data-cc-option], [data-cc-fixed-option]')]
        .sort((left, right) => Number(left.dataset.optionIndex) - Number(right.dataset.optionIndex))
        .map(option => option.dataset.optionValueId || option.selectedOptions[0]?.dataset.optionValueId);
    }
    async loadVariant() {
      this.request?.abort();
      this.request = new AbortController();
      const signal = this.request.signal;
      const selected = this.selectedOptionValues();
      const url = new URL(this.root.dataset.productUrl, location.origin);
      for (const name of ['preview_theme_id', 'preview_key', '_fd', 'pb']) {
        const value = new URL(location.href).searchParams.get(name);
        if (value) url.searchParams.set(name, value);
      }
      url.searchParams.set('section_id', this.root.dataset.sectionId);
      url.searchParams.set('option_values', selected.join(','));
      this.root.setAttribute('aria-busy', 'true');
      this.idInput.disabled = true;
      this.error.hidden = true;
      this.root.querySelectorAll('[data-cc-atc]').forEach(button => {button.disabled = true; button.dataset.ccAvailable = 'false';});
      try {
        const response = await fetch(url, {signal});
        if (!response.ok) throw new Error('Option request failed');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const updated = page.querySelector('[data-cc-product]');
        if (!updated) throw new Error('Missing product section');
        const data = JSON.parse(updated.querySelector('[data-cc-product-data]').textContent);
        for (const current of this.root.querySelectorAll('[data-cc-option], [data-cc-fixed-option]')) {
          const replacement = [...updated.querySelectorAll('[data-cc-option], [data-cc-fixed-option]')].find(item => item.id === current.id);
          if (!replacement) continue;
          if (current.matches('[data-cc-fixed-option]')) {
            current.value = replacement.value;
            current.dataset.optionValueId = replacement.dataset.optionValueId;
          } else current.innerHTML = replacement.innerHTML;
        }
        this.updateVariant(data, true);
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.error.textContent = this.root.dataset.loadError;
          this.error.hidden = false;
        }
      } finally {
        if (!signal.aborted) this.root.removeAttribute('aria-busy');
      }
    }
    updateVariant(data, updateURL) {
      this.data = data;
      const variant = data.variant;
      const available = Boolean(variant?.available);
      this.idInput.value = variant?.id || '';
      this.idInput.disabled = !variant;
      this.root.querySelectorAll('[data-cc-atc]').forEach(button => {button.disabled = !available; button.dataset.ccAvailable = String(available);});
      this.root.querySelectorAll('[data-cc-atc-label]').forEach(label => label.textContent = !variant ? this.root.dataset.unavailableLabel : available ? this.root.dataset.addLabel : this.root.dataset.soldOutLabel);
      this.root.querySelector('[data-cc-price]').textContent = variant ? this.format(variant.price) : this.root.dataset.unavailableLabel;
      const promotion = variant && Number(variant.compare_at_price) > Number(variant.price);
      const compare = this.root.querySelector('[data-cc-compare]');
      const saving = this.root.querySelector('[data-cc-saving]');
      compare.hidden = !promotion;
      saving.hidden = !promotion;
      if (promotion) {
        compare.textContent = this.format(variant.compare_at_price);
        this.root.querySelector('[data-cc-saving-amount]').textContent = `${this.root.dataset.saveLabel} ${this.format(variant.compare_at_price - variant.price)}`;
        this.root.querySelector('[data-cc-saving-percent]').textContent = `${Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100)}% ${this.root.dataset.offLabel}`;
      }
      const rule = data.quantityRule || {};
      this.quantity.min = rule.min || 1;
      this.quantity.step = rule.increment || 1;
      if (rule.max) this.quantity.max = rule.max;
      else this.quantity.removeAttribute('max');
      this.updateQuantity();
      if (data.mediaId) this.showMedia(String(data.mediaId));
      if (updateURL) {
        const url = new URL(location.href);
        url.searchParams.delete('option_values');
        if (variant) url.searchParams.set('variant', variant.id);
        else {
          url.searchParams.delete('variant');
          const values = this.selectedOptionValues();
          url.searchParams.set('option_values', values.join(','));
        }
        history.replaceState({}, '', url);
        this.root.dispatchEvent(new CustomEvent('cc:variant-change', {bubbles: true, detail: {variant}}));
      }
    }
    updateQuantity() {
      const min = Number(this.quantity.min) || 1;
      const step = Number(this.quantity.step) || 1;
      const max = this.quantity.hasAttribute('max') ? Number(this.quantity.max) : 9999;
      let value = Math.max(min, Math.min(max, Math.floor(Number(this.quantity.value)) || min));
      value = min + Math.floor((value - min) / step) * step;
      this.quantity.value = value;
      this.root.querySelector('[data-cc-quantity-step="-1"]').disabled = value <= min;
      this.root.querySelector('[data-cc-quantity-step="1"]').disabled = value + step > max;
      this.root.querySelectorAll('[data-cc-total]').forEach(total => {
        total.textContent = this.data.variant ? this.format(Number(this.data.variant.price) * value) : '';
      });
    }
    click(event) {
      const quantity = event.target.closest('[data-cc-quantity-step]');
      if (quantity) {
        this.quantity.value = Number(this.quantity.value) + Number(quantity.dataset.ccQuantityStep) * (Number(this.quantity.step) || 1);
        this.updateQuantity();
      }
      const image = event.target.closest('[data-cc-image]');
      if (image) this.showMedia(image.dataset.ccImage);
      const arrow = event.target.closest('[data-cc-image-step]');
      if (arrow) {
        const media = [...this.root.querySelectorAll('[data-cc-media]')];
        const index = media.findIndex(item => !item.hidden);
        this.showMedia(media[(index + Number(arrow.dataset.ccImageStep) + media.length) % media.length].dataset.ccMedia);
      }
      const tab = event.target.closest('[data-faq-tab]');
      if (tab) this.activateTab(tab.dataset.faqTab);
      if (event.target.closest('[data-cc-zoom]')) this.openZoom();
      if (event.target.closest('[data-cc-zoom-close]')) this.root.querySelector('[data-cc-zoom-dialog]').close();
      const dialog = this.root.querySelector('[data-cc-zoom-dialog]');
      if (event.target === dialog) dialog.close();
    }
    showMedia(id) {
      const active = [...this.root.querySelectorAll('[data-cc-media]')].find(item => item.dataset.ccMedia === id);
      if (!active) return;
      this.root.querySelectorAll('[data-cc-media]').forEach(item => {
        item.hidden = item !== active;
        if (item.hidden) item.querySelectorAll('video').forEach(video => video.pause());
      });
      this.root.querySelectorAll('[data-cc-image]').forEach(button => {
        const selected = button.dataset.ccImage === id;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      const image = active.querySelector('img');
      const stickyImage = this.root.querySelector('.pdp-sticky-photo');
      if (image && stickyImage) {stickyImage.src = image.currentSrc || image.src; stickyImage.removeAttribute('srcset');}
    }
    openZoom() {
      const source = this.root.querySelector('[data-cc-media]:not([hidden]) img');
      const dialog = this.root.querySelector('[data-cc-zoom-dialog]');
      if (!source || !dialog) return;
      const image = dialog.querySelector('img');
      image.src = source.closest('[data-cc-zoom]')?.dataset.ccZoomSrc || source.currentSrc || source.src;
      image.alt = source.alt;
      dialog.showModal();
      this.syncDialogLock();
    }
    syncDialogLock() {
      document.documentElement.classList.toggle('cc-modal-open', Boolean(document.querySelector('dialog[open]')));
    }
    activateTab(category, focus = false) {
      this.root.querySelectorAll('[data-faq-tab]').forEach(tab => {
        const selected = tab.dataset.faqTab === category;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected && focus) tab.focus();
      });
      this.root.querySelectorAll('[data-faq-panel]').forEach(panel => panel.hidden = panel.dataset.faqPanel !== category);
    }
    keydown(event) {
      const tab = event.target.closest('[data-faq-tab]');
      if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const tabs = [...this.root.querySelectorAll('[data-faq-tab]')];
      const current = tabs.indexOf(tab);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      this.activateTab(tabs[next].dataset.faqTab, true);
    }
    setStickyObserver() {
      if (!this.sticky) return;
      this.observer = new IntersectionObserver(() => this.updateSticky());
      this.observer.observe(this.primary);
      const footer = document.querySelector('.footer-newsletter, .site-footer');
      if (footer) this.observer.observe(footer);
      this.updateSticky();
    }
    scheduleSticky() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {this.frame = null; this.updateSticky();});
    }
    updateSticky() {
      if (!this.sticky) return;
      const bottom = this.primary.getBoundingClientRect().bottom;
      const header = document.querySelector('.site-header');
      const threshold = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      const footer = document.querySelector('.footer-newsletter, .site-footer');
      const footerVisible = footer && footer.getBoundingClientRect().top <= window.innerHeight;
      const visible = bottom < threshold && !footerVisible;
      this.sticky.hidden = !visible;
      document.documentElement.classList.toggle('has-product-sticky', visible);
      document.documentElement.style.setProperty('--product-sticky-height', visible ? `${this.sticky.offsetHeight}px` : '0px');
    }
    async loadRecommendations() {
      const target = this.root.querySelector('[data-cc-recommendations]');
      if (!target || target.dataset.mode === 'manual' || target.dataset.mode === 'automatic') return;
      this.recommendationObserver = new IntersectionObserver(async entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.recommendationObserver.disconnect();
        try {
          const response = await fetch(target.dataset.url, {signal: this.controller.signal});
          if (!response.ok) return;
          const page = new DOMParser().parseFromString(await response.text(), 'text/html');
          const content = page.querySelector('[data-cc-recommendations]');
          if (content?.childElementCount && content.dataset.mode === 'automatic') {target.innerHTML = content.innerHTML; target.hidden = false; target.dataset.mode = 'automatic';}
        } catch (_) {}
      }, {rootMargin: '500px'});
      this.recommendationObserver.observe(this.root.querySelector('#pdp-questions') || this.root.querySelector('.pdp-description') || this.primary);
    }
    destroy() {
      const zoom = this.root.querySelector('[data-cc-zoom-dialog]');
      if (zoom?.open) zoom.close();
      this.syncDialogLock();
      this.controller.abort();
      this.request?.abort();
      this.observer?.disconnect();
      this.recommendationObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      document.documentElement.classList.remove('has-product-sticky');
      document.documentElement.style.removeProperty('--product-sticky-height');
    }
  }
  function init(scope = document) {
    scope.querySelectorAll('[data-cc-product]').forEach(root => {
      if (!products.has(root)) products.set(root, new CCProduct(root));
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, instance] of products) {
      if (event.target.contains(root)) {instance.destroy(); products.delete(root);}
    }
  });
  document.addEventListener('shopify:block:select', event => {
    const panel = event.target.closest('[data-faq-panel]');
    const root = event.target.closest('[data-cc-product]');
    if (panel && root) products.get(root)?.activateTab(panel.dataset.faqPanel);
  });
})();
